import { SourceDataVerificationPermission } from '@/api/dtos';
import ConfigurationForm from '@/components/data/configuration/ConfigurationForm';
import Breadcrumbs from '@/components/layouts/Breadcrumbs';
import PageTitle, { TITLE_SEPARATOR } from '@/components/layouts/PageTitle';
import SkeletonExt from '@/components/layouts/SkeletonExt';
import TrialContext from '@/contexts/data/TrialContext';
import useConfigurationById from '@/hooks/data/useConfigurationById';
import useMetadata from '@/hooks/data/useMetadata';
import useTrialConfigurationByTrialCode from '@/hooks/data/useTrialConfigurationByTrialCode';
import usePermissions from '@/hooks/utility/usePermissions';
import { faDatabase } from '@fortawesome/pro-duotone-svg-icons';
import { Grid, Paper, styled, Typography } from '@mui/material';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';



const EditConfiguration: FC = () => {
    const { trialCode, configurationId } = useParams();
    
    const { data: trial } = useContext(TrialContext);
    const { data: metadata, loading: metadataLoading, validating: metadataUpdating } = useMetadata(trialCode!, { revalidateOnFocus: false });
    const { data: configuration, loading: configurationLoading, validating: configurationUpdating, mutate: configurationMutate } = useConfigurationById(metadata ? +configurationId! : undefined, { revalidateOnFocus: false });
    const { data: trialConfiguration, loading: trialConfigurationLoading, validating: trialConfigurationUpdating } = useTrialConfigurationByTrialCode(!!metadata ? trialCode : undefined, { revalidateOnFocus: false });

    const [ready, setReady] = useState(false);

    useEffect(() => {
        const timeoutId = setTimeout(() => setReady(true), 250);

        return () => {
            clearTimeout(timeoutId);
        }
    }, [])

    const breadcrumbs = useMemo(() => {
        return [
            {
                label: 'Configurations',
                link: `/${trialCode}/configurations`,
                icon: faDatabase
            },
            {
                label: configurationLoading ? <SkeletonExt maxWidth={240} minWidth={120} /> : configuration?.name,
                icon: faDatabase
            }
        ];
    }, [trialCode, configurationLoading])

    const loadingData = !ready || metadataLoading || configurationLoading || trialConfigurationLoading;
    const updating = !loadingData && (metadataUpdating || configurationUpdating || trialConfigurationUpdating);

    const { 
        data: [
            hasEditConfiguration
        ] = [], 
        loading: permissionsLoading, 
        validating: permissionsValidating
    } = usePermissions(
        [
            SourceDataVerificationPermission.EditConfiguration
        ], 
        loadingData,
        trial?.id,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        {
            revalidateOnFocus: false
        }
    );

    const loading = loadingData || permissionsLoading || permissionsValidating;

    if (!hasEditConfiguration && !permissionsLoading && !permissionsValidating) {
        return (
            <Navigate to='/error/403' />
        )
    }

    return (
        <>
            <PageTitle title={`${configuration?.name ? configuration?.name : 'Edit'} Configuration${TITLE_SEPARATOR}${trial?.name}`} />
            <Breadcrumbs 
                breadcrumbs={breadcrumbs}
            />
            <Typography variant="h1" gutterBottom>
                Edit Configuration
            </Typography>
            <ConfigurationForm
                configuration={ready ? configuration : undefined}
                trialConfiguration={ready ? trialConfiguration : undefined}
                updateConfiguration={configurationMutate}
                trial={trial}
                metadata={ready ? metadata : undefined}
                loading={loading}
                updating={updating}
            />
        </>
    );
}

export default EditConfiguration;